import { z } from 'zod';
import {
  API_V1,
  FlightReply,
  FlyReply,
  type FlyRequest,
  type TrainRequest,
  RailCompanyReply,
  PlayerTrainsReply,
  BankReply,
  HousesReply,
  BettingReply,
  HealthReply,
  StaffReply,
  CarsReply,
  HustleReply,
  HustleDecorView,
  GistActionReply,
  GistFeedReply,
  GistFollowReply,
  GistNoticesReply,
  GistSearchReply,
  GistTrendsReply,
  GistUnreadReply,
  GistVoiceReply,
  GistPhotoReply,
  GistPhotosReply,
  type GistPhotoDecision,
  type GistSearchKind,
  GistNewsReply,
  GistPostReply,
  GistReportsReply,
  GistThreadReply,
  type GistAction,
  type GistDecision,
  type GistPostRequest,
  type GistTab,
  type DecorLayout,
  HustleBoardReply,
  HustleProsReply,
  HustleReportsReply,
  type HustleKind,
  type HustlePostRequest,
  type HustleOfferRequest,
  type HustleAction,
  type HustleListingRequest,
  type HustleDecision,
  type BuyCarRequest,
  FamilyReply,
  type ChildCareRequest,
  type FamilyAskRequest,
  CoursesReply,
  QuizView,
  QuizAnswerReply,
  PlaceBetRequest,
  BettingControlRequest,
  RentalsReply,
  BuyRentalRequest,
  RentalActionRequest,
  type HouseAction,
  MoveHomeRequest,
  BondRequest,
  MoneyHistoryReply,
  SavingsRequest,
  type StartRailCompanyRequest,
  type BuyTrainRequest,
  type TrainActionRequest,
  type RailCompanyActionRequest,
  type RateTripRequest,
  MyJetReply,
  type BuyJetRequest,
  type OfferSeatsRequest,
  SeatOffersReply,
  ApiError,
  AdminSessionSettingsReply,
  type AdminSessionSettings,
  AdminAdvertsReply,
  AdvertSettingsReply,
  AdvertsReply,
  BookAdvertReply,
  BusinessActionReply,
  BusinessesReply,
  ShopBuyReply,
  ShopsReply,
  type BusinessAction,
  type StartBusinessRequest,
  MyAdvertsReply,
  type AdvertDecision,
  type AdvertSettings,
  type BookAdvertRequest,
  NeighboursReply,
  VisitsReply,
  VisitHomeReply,
  type StartVisitRequest,
  type ApiError as ApiErrorShape,
  AdminListReply,
  AdminMeReply,
  AdminSessionReply,
  RecoveryCodesReply,
  TwoStepSetupReply,
  AuditListReply,
  CityActivitiesReply,
  WorldContentReply,
  AdminContentReply,
  type SaveDraftBody,
  MeReply,
  RoleListReply,
  SessionReply,
  type AdminCreate,
  type AdminRolesUpdate,
  type RoleCreate,
  type RoleUpdate,
  type SessionRequest,
  type TwoStepCode,
  PlayerProfile,
  type ProfileSetup,
  UsernameAvailability,
  type CompanyDetails,
  CompanyDetailsReply,
  CharacterOptionsReply,
  EconomySettings,
  EconomySettingsReply,
  EconomySummaryReply,
  IDEMPOTENCY_HEADER,
  LedgerAccountView,
  LedgerApprovalsReply,
  LedgerEntriesReply,
  LedgerTransactionReply,
  MoneyActionReply,
  MyLifeReply,
  AccountReply,
  PlayerActionReply,
  PlayerDetailReply,
  PlayerListReply,
  PlayerWarning,
  type PlayerAction,
  GoalsReply,
  HomeActionReply,
  ChatListReply,
  ChatForwardReply,
  ChatReportsReply,
  TeamAdminReply,
  NoticesReply,
  PushKeyReply,
  type PushSubscribe,
  TeamReply,
  TeamVoteReply,
  type TeamPostCreate,
  type ChatReportDecision,
  GroupReply,
  type GroupAction,
  type GroupCreate,
  ChatMessageReply,
  Conversation,
  ChatMessagesReply,
  ChatOpenReply,
  type ChatOpen,
  type ChatSend,
  PeopleReply,
  PlaceHereReply,
  PersonActionReply,
  type PersonAction,
  MyHomeReply,
  type HomeLayout,
  type DreamKey,
  type MissionKey,
  type PerkKey,
  AdminStatsReply,
  PublicStatsReply,
  type Presence,
  type SiteStatsSettings,
  LookChangeReply,
  PlayerSettingsReply,
  type DisplayNameChange,
  type LookChange,
  type PlayerSettings,
  type TraitsUpdate,
  type UsernameChange,
  PlayReply,
  PlayerMoneyReply,
  type AdjustmentRequest,
  type ApprovalDecision,
  type LifeCreate,
  type AccountDelete,
  type WorldFacts,
  WorldFactsReply,
  ConsentSettingsReply,
  type ConsentSettingsUpdate,
  type TravelMode,
  AdminTransferView,
  AdminTransfersReply,
  MoneyAllowanceReply,
  MoneyMessageReply,
  GovBannerReply,
  GovReply,
  GovReportReply,
  GovTeamReply,
  type GovPolicyId,
  type GovReportReason,
  type GovTeamAction,
  type AskMoneyRequest,
  type BuyMealRequest,
  type MoneyRequestAnswer,
  type SendMoneyRequest,
  type TransferDecision,
} from '@london-life/contracts';
import { CSRF_HEADER } from '@london-life/auth';
import { ClientError, kindForApiCode, type ErrorKind } from '@london-life/errors';

// when a reply has no readable body, its status still says what kind of failure it was
function kindForStatus(status: number): ErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  if (status >= 400 && status < 500) return 'validation';
  return 'server';
}

// every failure from the API, typed: the HTTP status (0 when the request never got a reply) plus the
// shared error kind, code, request id and field messages; screens read it through describeError
export class ApiRequestError extends ClientError {
  readonly body: ApiErrorShape['error'] | null;
  constructor(status: number, kind: ErrorKind, body: ApiErrorShape['error'] | null, requestId?: string) {
    super({
      kind,
      status,
      code: body?.code,
      requestId: body?.requestId ?? requestId,
      fields: fieldMessages(body?.details),
      serverMessage: body?.message,
    });
    this.name = 'ApiRequestError';
    this.body = body;
  }
}

// zod's flattened field errors from the API become one message per field
function fieldMessages(details: unknown): Record<string, string> | undefined {
  const fieldErrors = (details as { fieldErrors?: Record<string, string[] | undefined> } | null)?.fieldErrors;
  if (!fieldErrors) return undefined;
  const fields: Record<string, string> = {};
  for (const [field, messages] of Object.entries(fieldErrors)) if (messages?.[0]) fields[field] = messages[0];
  return Object.keys(fields).length ? fields : undefined;
}

type Options = {
  baseUrl: string;
  getCsrfToken?: () => string | null;
  fetchImpl?: typeof fetch;
  timeoutMs?: number;
  isOnline?: () => boolean;
  // called when the API says the session has ended, so the app can return to its sign-in screen
  onUnauthorized?: (message: string) => void;
  // called when the API asks for a two-step code before a dangerous action; resolve true once the code
  // has been entered and the request is retried once
  onTwoStepRequired?: () => Promise<boolean>;
};
type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

// a query string from the parts that are set ("" when none are)
const searchOf = (query: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query))
    if (value !== undefined && value !== '') search.set(key, String(value));
  const text = search.toString();
  return text ? `?${text}` : '';
};
const browserOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false);

// one typed client shared by the player app and the admin dashboard
export function createApiClient({
  baseUrl,
  getCsrfToken,
  fetchImpl = fetch,
  timeoutMs = 20_000,
  isOnline = browserOnline,
  onUnauthorized,
  onTwoStepRequired,
}: Options) {
  type Init = {
    method?: Method;
    body?: unknown;
    query?: Record<string, string | number | undefined>;
    // money actions send a fresh key per action; a retry (for example after a two-step prompt) reuses it
    idempotencyKey?: string;
    // a file sent as it is (a voice note), with its own type, instead of JSON
    raw?: { data: Blob; type: string };
  };
  // one request; if the API asks for a fresh two-step code, ask the app for it, then try once more
  async function request<T>(schema: z.ZodType<T>, path: string, init: Init = {}): Promise<T> {
    try {
      return await attempt(schema, path, init);
    } catch (error) {
      const twoStep = error instanceof ApiRequestError && error.code === 'two_step_required';
      if (twoStep && onTwoStepRequired && path !== API_V1.adminTwoStepVerify && (await onTwoStepRequired()))
        return attempt(schema, path, init);
      throw error;
    }
  }
  async function attempt<T>(schema: z.ZodType<T>, path: string, init: Init = {}): Promise<T> {
    const url = new URL(path, baseUrl);
    for (const [key, value] of Object.entries(init.query ?? {}))
      if (value !== undefined) url.searchParams.set(key, String(value));
    const headers: Record<string, string> = { Accept: 'application/json' };
    const method = init.method ?? 'GET';
    if (init.raw) headers['Content-Type'] = init.raw.type;
    else if (init.body !== undefined) headers['Content-Type'] = 'application/json';
    const csrf = method !== 'GET' ? getCsrfToken?.() : null;
    if (csrf) headers[CSRF_HEADER] = csrf;
    if (init.idempotencyKey) headers[IDEMPOTENCY_HEADER] = init.idempotencyKey;

    // no reply at all: offline, unreachable, blocked by the browser, or too slow
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let response: Response;
    try {
      response = await fetchImpl(url, {
        method,
        headers,
        credentials: 'include',
        body: init.raw ? init.raw.data : init.body === undefined ? undefined : JSON.stringify(init.body),
        signal: controller.signal,
      });
    } catch {
      const kind: ErrorKind = controller.signal.aborted ? 'timeout' : isOnline() ? 'network' : 'offline';
      throw new ApiRequestError(0, kind, null);
    } finally {
      clearTimeout(timer);
    }

    // a reply that is not our JSON (a proxy error page, a cut-off body) is a server problem, not a crash
    const requestId = response.headers.get('x-request-id') ?? undefined;
    const text = await response.text();
    let json: unknown = {};
    try {
      json = text ? JSON.parse(text) : {};
    } catch {
      throw new ApiRequestError(
        response.status,
        response.ok ? 'server' : kindForStatus(response.status),
        null,
        requestId,
      );
    }
    if (!response.ok) {
      const parsed = ApiError.safeParse(json);
      if (response.status === 401 && path !== API_V1.me)
        onUnauthorized?.(parsed.success ? parsed.data.error.message : '');
      if (parsed.success)
        throw new ApiRequestError(
          response.status,
          kindForApiCode(parsed.data.error.code),
          parsed.data.error,
          requestId,
        );
      throw new ApiRequestError(response.status, kindForStatus(response.status), null, requestId);
    }
    const reply = schema.safeParse(json);
    if (!reply.success) throw new ApiRequestError(response.status, 'server', null, requestId);
    return reply.data;
  }
  const empty = z.object({ ok: z.literal(true) });
  // a picture from the API (an advert's, a Gist photo), as a file the page can draw: pages only show pictures of
  // their own
  async function picture(path: string): Promise<Blob> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const response = await fetchImpl(new URL(path, baseUrl), { credentials: 'include', signal: controller.signal });
      if (!response.ok) throw new ApiRequestError(response.status, kindForStatus(response.status), null);
      return await response.blob();
    } catch (error) {
      if (error instanceof ApiRequestError) throw error;
      throw new ApiRequestError(0, controller.signal.aborted ? 'timeout' : isOnline() ? 'network' : 'offline', null);
    } finally {
      clearTimeout(timer);
    }
  }

  return {
    request,
    // a picture the API serves (an advert's, a Gist photo), as a file the page can show
    picture,
    createSession: (body: SessionRequest) => request(SessionReply, API_V1.authSession, { method: 'POST', body }),
    signOut: () => request(empty, API_V1.authSignOut, { method: 'POST' }),
    me: () => request(MeReply, API_V1.me),
    profile: {
      setUp: (body: ProfileSetup) =>
        request(z.object({ profile: PlayerProfile }), API_V1.meProfile, { method: 'POST', body }),
      usernameAvailability: (username: string) =>
        request(UsernameAvailability, API_V1.usernameAvailability(encodeURIComponent(username))),
    },
    loadCityActivities: () => request(CityActivitiesReply, API_V1.cityActivities),
    // the published content: places, what is inside them and what can be done there
    worldContent: () => request(WorldContentReply, API_V1.worldContent),
    characterOptions: () => request(CharacterOptionsReply, API_V1.characterOptions),
    life: {
      mine: () => request(MyLifeReply, API_V1.meLife),
      // the creator keeps one key per attempt, so a double click or a retry starts one life
      start: (body: LifeCreate, idempotencyKey: string) =>
        request(MyLifeReply, API_V1.meLife, { method: 'POST', body, idempotencyKey }),
      end: () => request(MyLifeReply, API_V1.meLifeEnd, { method: 'POST', body: { confirm: true } }),
      traits: (body: TraitsUpdate) => request(MyLifeReply, API_V1.meLifeTraits, { method: 'PUT', body }),
      // a paid look change: one key per attempt, so a retry never charges twice
      look: (body: LookChange, idempotencyKey: string) =>
        request(LookChangeReply, API_V1.meLifeLook, { method: 'POST', body, idempotencyKey }),
    },
    // the Goals tab: rewards are paid once each
    goals: {
      get: () => request(GoalsReply, API_V1.meGoals),
      claimMission: (mission: MissionKey) =>
        request(GoalsReply, API_V1.meGoalsMission, { method: 'POST', body: { mission } }),
      chooseDream: (dream: DreamKey) => request(GoalsReply, API_V1.meGoalsDream, { method: 'PUT', body: { dream } }),
      claimDream: () => request(GoalsReply, API_V1.meGoalsDreamClaim, { method: 'POST', body: {} }),
      buyPerk: (perk: PerkKey) => request(GoalsReply, API_V1.meGoalsPerk, { method: 'POST', body: { perk } }),
    },
    // the player's house: buying pays through the ledger, so it carries a key
    home: {
      get: () => request(MyHomeReply, API_V1.meHome),
      arrange: (body: HomeLayout) => request(HomeActionReply, API_V1.meHomeLayout, { method: 'PUT', body }),
      buy: (item: string, idempotencyKey: string) =>
        request(HomeActionReply, API_V1.meHomeBuy, { method: 'POST', body: { item }, idempotencyKey }),
      sell: (id: string) => request(HomeActionReply, API_V1.meHomeSell, { method: 'POST', body: { id } }),
      payRent: (idempotencyKey: string) =>
        request(HomeActionReply, API_V1.meHomeRent, { method: 'POST', body: {}, idempotencyKey }),
    },
    // people: who you know and who is here; a social action carries a key (it changes both people once)
    people: {
      get: () => request(PeopleReply, API_V1.mePeople),
      // who is at a place right now (the place's card on the map)
      here: (placeId: string) => request(PlaceHereReply, API_V1.mePlaceHere(placeId)),
      act: (lifeId: string, body: PersonAction, idempotencyKey?: string) =>
        request(PersonActionReply, API_V1.mePersonActions(lifeId), { method: 'POST', body, idempotencyKey }),
    },
    // money between players, from a direct chat: every move carries a key, so a double tap sends once
    money: {
      allowance: (lifeId: string) => request(MoneyAllowanceReply, API_V1.meMoneyAllowance(lifeId)),
      send: (body: SendMoneyRequest, idempotencyKey: string) =>
        request(MoneyMessageReply, API_V1.meMoneySend, { method: 'POST', body, idempotencyKey }),
      ask: (body: AskMoneyRequest, idempotencyKey: string) =>
        request(MoneyMessageReply, API_V1.meMoneyAsk, { method: 'POST', body, idempotencyKey }),
      // "Buy them food"
      meal: (body: BuyMealRequest, idempotencyKey: string) =>
        request(MoneyMessageReply, API_V1.meMoneyMeal, { method: 'POST', body, idempotencyKey }),
      answer: (requestId: string, answer: MoneyRequestAnswer['answer'], idempotencyKey: string) =>
        request(MoneyMessageReply, API_V1.meMoneyRequest(requestId), {
          method: 'POST',
          body: { answer },
          idempotencyKey,
        }),
    },
    // visits: the Neighbours list, setting off to a home (a key: one journey per tap), the knock's answer, and
    // "Invite over" from a chat
    visits: {
      neighbours: () => request(NeighboursReply, API_V1.meNeighbours),
      get: () => request(VisitsReply, API_V1.meVisits),
      home: () => request(VisitHomeReply, API_V1.meVisitHome),
      start: (body: StartVisitRequest, idempotencyKey: string) =>
        request(VisitsReply, API_V1.meVisitStart, { method: 'POST', body, idempotencyKey }),
      leave: () => request(VisitsReply, API_V1.meVisitLeave, { method: 'POST', body: {} }),
      answer: (visitId: string, letIn: boolean) =>
        request(VisitsReply, API_V1.meVisitAnswer(visitId), { method: 'POST', body: { letIn } }),
      sendHome: (visitId: string) =>
        request(VisitsReply, API_V1.meVisitSendHome(visitId), { method: 'POST', body: {} }),
      invite: (lifeId: string, idempotencyKey: string) =>
        request(ChatMessageReply, API_V1.meVisitInvite, { method: 'POST', body: { lifeId }, idempotencyKey }),
    },
    // adverts on the map's boards: what is on them (anyone), and your own (book with a key: one booking per tap)
    adverts: {
      list: () => request(AdvertsReply, API_V1.adverts),
      mine: () => request(MyAdvertsReply, API_V1.meAdverts),
      book: (body: BookAdvertRequest, idempotencyKey: string) =>
        request(BookAdvertReply, API_V1.meAdverts, { method: 'POST', body, idempotencyKey }),
      withdraw: (advertId: string) =>
        request(MyAdvertsReply.extend({ walletPence: z.number().int() }), API_V1.meAdvertWithdraw(advertId), {
          method: 'POST',
          body: {},
        }),
      picture,
    },
    // businesses: starting one and moving money carry a key (once per tap)
    business: {
      list: () => request(BusinessesReply, API_V1.meBusinesses),
      start: (body: StartBusinessRequest, idempotencyKey: string) =>
        request(BusinessActionReply, API_V1.meBusinesses, { method: 'POST', body, idempotencyKey }),
      act: (businessId: string, body: BusinessAction, idempotencyKey?: string) =>
        request(BusinessActionReply, API_V1.meBusiness(businessId), { method: 'POST', body, idempotencyKey }),
      // players' shops, for those buying (one purchase per key)
      shops: () => request(ShopsReply, API_V1.meShops),
      share: (conversationId: string, businessId: string, idempotencyKey: string) =>
        request(ChatMessageReply, API_V1.meChatShop(conversationId), {
          method: 'POST',
          body: { businessId },
          idempotencyKey,
        }),
      buy: (businessId: string, productId: string, qty: number, idempotencyKey: string) =>
        request(ShopBuyReply, API_V1.meShopBuy(businessId), {
          method: 'POST',
          body: { productId, qty },
          idempotencyKey,
        }),
    },
    // Houses: the homes on the market, moving to one, buying, selling or extending your own
    houses: {
      market: () => request(HousesReply, API_V1.meHouses),
      move: (body: MoveHomeRequest, idempotencyKey: string) =>
        request(HousesReply, API_V1.meHousesMove, { method: 'POST', body, idempotencyKey }),
      act: (action: HouseAction, idempotencyKey: string) =>
        request(HousesReply, API_V1.meHousesAction, { method: 'POST', body: { action }, idempotencyKey }),
      lets: () => request(RentalsReply, API_V1.meLets),
      buyLet: (body: BuyRentalRequest, idempotencyKey: string) =>
        request(RentalsReply, API_V1.meLets, { method: 'POST', body, idempotencyKey }),
      letAct: (letId: string, body: RentalActionRequest, idempotencyKey: string) =>
        request(RentalsReply, API_V1.meLet(letId), { method: 'POST', body, idempotencyKey }),
    },
    // Bets: the day's games, placing a bet, and the safer-play controls (limit, break, reality check)
    // Courses: enrolling pays a fee (a key); the quiz is answered one question at a time
    courses: {
      mine: () => request(CoursesReply, API_V1.meCourses),
      enrol: (course: string, idempotencyKey: string) =>
        request(CoursesReply, API_V1.meCoursesEnrol, { method: 'POST', body: { course }, idempotencyKey }),
      quiz: (course: string) => request(QuizView, API_V1.meCourseQuiz(course), { method: 'POST', body: {} }),
      answer: (course: string, answer: string) =>
        request(QuizAnswerReply, API_V1.meCourseAnswer(course), { method: 'POST', body: { answer } }),
    },
    // Staff: who works for you, hiring (pays the first part-week, so a key), letting someone go
    staff: {
      mine: () => request(StaffReply, API_V1.meStaff),
      hire: (role: string, idempotencyKey: string) =>
        request(StaffReply, API_V1.meStaff, { method: 'POST', body: { role }, idempotencyKey }),
      letGo: (id: string) => request(StaffReply, API_V1.meStaffMember(id), { method: 'DELETE' }),
    },
    // Cars: the garage and the showroom; buying (trading the car driven in), selling, choosing, a service
    cars: {
      mine: () => request(CarsReply, API_V1.meCars),
      buy: (body: BuyCarRequest, idempotencyKey: string) =>
        request(CarsReply, API_V1.meCars, { method: 'POST', body, idempotencyKey }),
      drive: (id: string) => request(CarsReply, API_V1.meCar(id), { method: 'PUT' }),
      sell: (id: string) => request(CarsReply, API_V1.meCar(id), { method: 'DELETE' }),
      service: (id: string) => request(CarsReply, API_V1.meCarService(id), { method: 'POST' }),
    },
    // Family: partners and couples; a date, a proposal or the wedding asked of a partner, answered or taken back
    // Hustle: the Board, the pros, your jobs, posting, offering, acting on a job, listing yourself for hire
    hustle: {
      mine: () => request(HustleReply, API_V1.meHustle),
      board: (query: { kind?: HustleKind; q?: string; before?: string } = {}) =>
        request(HustleBoardReply, `${API_V1.meHustleBoard}${searchOf(query)}`),
      pros: (query: { kind?: HustleKind; q?: string } = {}) =>
        request(HustleProsReply, `${API_V1.meHustlePros}${searchOf(query)}`),
      post: (body: HustlePostRequest, idempotencyKey: string) =>
        request(HustleReply, API_V1.meHustleJobs, { method: 'POST', body, idempotencyKey }),
      offer: (body: HustleOfferRequest, idempotencyKey: string) =>
        request(HustleReply, API_V1.meHustleOffers, { method: 'POST', body, idempotencyKey }),
      act: (jobId: string, body: HustleAction) =>
        request(HustleReply, API_V1.meHustleJob(jobId), { method: 'POST', body }),
      list: (body: HustleListingRequest) => request(HustleReply, API_V1.meHustleListing, { method: 'PUT', body }),
      unlist: () => request(HustleReply, API_V1.meHustleListing, { method: 'DELETE' }),
      // the decorator's copy of a home: read it, and save the draft as the decorator works
      look: (jobId: string) => request(HustleDecorView, API_V1.meHustleLook(jobId)),
      saveLook: (jobId: string, body: DecorLayout) =>
        request(HustleDecorView, API_V1.meHustleLook(jobId), { method: 'PUT', body }),
    },
    // Gist: the feeds and profiles, a post and its thread and replies, posting, acting on a post, following;
    // search, the #tags trending, and Gist's notifications
    gist: {
      feed: (tab: GistTab, before?: string, of?: string) =>
        request(
          GistFeedReply,
          `${API_V1.meGist}${searchOf({ tab, ...(before ? { before } : {}), ...(of ? { of } : {}) })}`,
        ),
      thread: (postId: string) => request(GistThreadReply, API_V1.meGistPost(postId)),
      post: (body: GistPostRequest, idempotencyKey: string) =>
        request(GistPostReply, API_V1.meGist, { method: 'POST', body, idempotencyKey }),
      act: (postId: string, body: GistAction) =>
        request(GistActionReply, API_V1.meGistPost(postId), { method: 'POST', body }),
      follow: (lifeId: string, on: boolean) =>
        request(GistFollowReply, API_V1.meGistFollow(lifeId), { method: 'POST', body: { on } }),
      search: (q: string, kind: GistSearchKind = 'all') =>
        request(GistSearchReply, `${API_V1.meGistSearch}${searchOf({ q, kind })}`),
      trends: () => request(GistTrendsReply, API_V1.meGistTrends),
      notices: () => request(GistNoticesReply, API_V1.meGistNotices),
      seen: () => request(GistUnreadReply, API_V1.meGistNoticesSeen, { method: 'POST', body: {} }),
      unread: () => request(GistUnreadReply, API_V1.meGistUnread),
      // a photo from the in-game camera, as it is (the server checks it and makes it again); then put on a post
      photo: (picture: Blob) =>
        request(GistPhotoReply, API_V1.meGistPhoto, { method: 'POST', raw: { data: picture, type: picture.type } }),
      // a voice note: the recording as it is, with its length; then put on a post or reply by its id
      voice: (recording: Blob, type: string, ms: number) =>
        request(GistVoiceReply, API_V1.meGistVoice, {
          method: 'POST',
          raw: { data: recording, type },
          query: { ms: Math.round(ms) },
        }),
    },
    // Gov: the State House and the race; standing, voting at City Hall, the Mayor's policy and announcement
    gov: {
      state: () => request(GovReply, API_V1.meGov),
      stand: (slogan: string, idempotencyKey: string) =>
        request(GovReply, API_V1.meGovStand, { method: 'POST', body: { slogan }, idempotencyKey }),
      vote: (candidateId: string) => request(GovReply, API_V1.meGovVote, { method: 'POST', body: { candidateId } }),
      policy: (policy: GovPolicyId) => request(GovReply, API_V1.meGovPolicy, { method: 'POST', body: { policy } }),
      announce: (text: string, idempotencyKey: string) =>
        request(GovReply, API_V1.meGovAnnounce, { method: 'POST', body: { text }, idempotencyKey }),
      report: (announcementId: string, reason: GovReportReason) =>
        request(GovReportReply, API_V1.meGovReport, { method: 'POST', body: { announcementId, reason } }),
    },
    family: {
      mine: () => request(FamilyReply, API_V1.meFamily),
      ask: (body: FamilyAskRequest, idempotencyKey: string) =>
        request(FamilyReply, API_V1.meFamilyAsks, { method: 'POST', body, idempotencyKey }),
      answer: (id: string, yes: boolean) =>
        request(FamilyReply, API_V1.meFamilyAnswer(id), { method: 'POST', body: { yes } }),
      withdraw: (id: string) => request(FamilyReply, API_V1.meFamilyAsk(id), { method: 'DELETE' }),
      care: (childId: string, action: ChildCareRequest['action'], idempotencyKey: string) =>
        request(FamilyReply, API_V1.meFamilyChildCare(childId), {
          method: 'POST',
          body: { action },
          idempotencyKey,
        }),
      nanny: (idempotencyKey: string) =>
        request(FamilyReply, API_V1.meFamilyNanny, { method: 'POST', body: {}, idempotencyKey }),
    },
    // Health: how you are, and the pharmacy's medicine (delivered)
    health: {
      mine: () => request(HealthReply, API_V1.meHealth),
      pharmacy: (idempotencyKey: string) =>
        request(HealthReply, API_V1.meHealthPharmacy, { method: 'POST', body: {}, idempotencyKey }),
    },
    bets: {
      mine: () => request(BettingReply, API_V1.meBets),
      place: (body: PlaceBetRequest, idempotencyKey: string) =>
        request(BettingReply, API_V1.meBets, { method: 'POST', body, idempotencyKey }),
      control: (body: BettingControlRequest) => request(BettingReply, API_V1.meBetsControl, { method: 'POST', body }),
    },
    // the Bank: savings, bonds and the money history
    bank: {
      mine: () => request(BankReply, API_V1.meBank),
      savings: (body: SavingsRequest, idempotencyKey: string) =>
        request(BankReply, API_V1.meBankSavings, { method: 'POST', body, idempotencyKey }),
      bond: (body: BondRequest, idempotencyKey: string) =>
        request(BankReply, API_V1.meBankBonds, { method: 'POST', body, idempotencyKey }),
      cash: (bondId: string) => request(BankReply, API_V1.meBankBond(bondId), { method: 'POST', body: {} }),
      history: (before?: number) =>
        request(MoneyHistoryReply, before ? `${API_V1.meBankHistory}?before=${before}` : API_V1.meBankHistory),
    },
    // Rail Co.: my train company and its trains; players' trains in the ticket office; stars for a ride
    railco: {
      mine: () => request(RailCompanyReply, API_V1.meRailCo),
      start: (body: StartRailCompanyRequest, idempotencyKey: string) =>
        request(RailCompanyReply, API_V1.meRailCo, { method: 'POST', body, idempotencyKey }),
      buy: (body: BuyTrainRequest, idempotencyKey: string) =>
        request(RailCompanyReply, API_V1.meRailCoTrains, { method: 'POST', body, idempotencyKey }),
      train: (trainId: string, body: TrainActionRequest, idempotencyKey: string) =>
        request(RailCompanyReply, API_V1.meRailCoTrain(trainId), { method: 'POST', body, idempotencyKey }),
      act: (body: RailCompanyActionRequest, idempotencyKey: string) =>
        request(RailCompanyReply, API_V1.meRailCoAction, { method: 'POST', body, idempotencyKey }),
      offers: () => request(PlayerTrainsReply, API_V1.railOffers),
      rate: (body: RateTripRequest) =>
        request(z.object({ ok: z.boolean(), rated: z.boolean() }), API_V1.meRailRate, { method: 'POST', body }),
    },
    // chat: sending carries a key (one message per tap); everything else is plain
    chat: {
      list: () => request(ChatListReply, API_V1.meChats),
      open: (body: ChatOpen) => request(ChatOpenReply, API_V1.meChatOpen, { method: 'POST', body }),
      older: (conversationId: string, before: string) =>
        request(ChatMessagesReply, `${API_V1.meChatMessages(conversationId)}?before=${encodeURIComponent(before)}`),
      send: (conversationId: string, body: ChatSend, idempotencyKey: string) =>
        request(ChatMessageReply, API_V1.meChatMessages(conversationId), { method: 'POST', body, idempotencyKey }),
      edit: (messageId: string, text: string) =>
        request(ChatMessageReply, API_V1.meChatMessage(messageId), { method: 'PATCH', body: { text } }),
      remove: (messageId: string) => request(ChatMessageReply, API_V1.meChatMessage(messageId), { method: 'DELETE' }),
      react: (messageId: string, emoji: string | null) =>
        request(ChatMessageReply, API_V1.meChatMessageReaction(messageId), { method: 'PUT', body: { emoji } }),
      forward: (messageId: string, conversationIds: string[], idempotencyKey: string) =>
        request(ChatForwardReply, API_V1.meChatMessageForward(messageId), {
          method: 'POST',
          body: { conversationIds },
          idempotencyKey,
        }),
      createGroup: (body: GroupCreate) => request(GroupReply, API_V1.meChatGroups, { method: 'POST', body }),
      group: (conversationId: string, body: GroupAction) =>
        request(GroupReply, API_V1.meChatGroup(conversationId), { method: 'POST', body }),
      read: (conversationId: string) =>
        request(z.object({ ok: z.boolean() }), API_V1.meChatRead(conversationId), { method: 'POST', body: {} }),
      readAll: () => request(z.object({ ok: z.boolean() }), API_V1.meChatReadAll, { method: 'POST', body: {} }),
      // a voice note: the recording as it is, with its length
      voice: (conversationId: string, recording: Blob, type: string, ms: number, idempotencyKey: string) =>
        request(ChatMessageReply, API_V1.meChatVoice(conversationId), {
          method: 'POST',
          raw: { data: recording, type },
          query: { ms: Math.round(ms) },
          idempotencyKey,
        }),
      allowVoice: (conversationId: string, allow: boolean) =>
        request(
          z.object({ conversation: Conversation, message: z.string() }),
          API_V1.meChatVoiceAllow(conversationId),
          {
            method: 'PUT',
            body: { allow },
          },
        ),
      // the London Life team chat
      team: () => request(TeamReply, API_V1.meTeam),
      teamRead: () => request(z.object({ ok: z.boolean() }), API_V1.meTeamRead, { method: 'POST', body: {} }),
      teamVote: (postId: string, optionId: string) =>
        request(TeamVoteReply, API_V1.meTeamVote(postId), { method: 'POST', body: { optionId } }),
      clear: (conversationId: string, hide: boolean) =>
        request(z.object({ ok: z.boolean() }), API_V1.meChatClear(conversationId), { method: 'POST', body: { hide } }),
      report: (messageId: string, reason: string) =>
        request(z.object({ ok: z.boolean(), message: z.string() }), API_V1.meChatMessageReport(messageId), {
          method: 'POST',
          body: { reason },
        }),
    },
    // warnings from the moderators, shown once
    // alerts when the game is closed
    pushKey: () => request(PushKeyReply, API_V1.publicPushKey),
    pushSubscribe: (body: PushSubscribe) =>
      request(z.object({ ok: z.boolean() }), API_V1.mePush, { method: 'POST', body }),
    pushUnsubscribe: (endpoint: string) =>
      request(z.object({ ok: z.boolean() }), API_V1.mePush, { method: 'DELETE', body: { endpoint } }),
    // the bell
    notices: () => request(NoticesReply, API_V1.meNotices),
    readNotices: () => request(z.object({ ok: z.boolean() }), API_V1.meNoticesRead, { method: 'POST', body: {} }),
    warnings: () => request(z.object({ warnings: z.array(PlayerWarning) }), API_V1.meWarnings),
    warningSeen: (warningId: string) =>
      request(z.object({ ok: z.boolean() }), API_V1.meWarningSeen(warningId), { method: 'POST', body: {} }),
    // the profile screen
    account: {
      get: () => request(AccountReply, API_V1.meAccount),
      username: (body: UsernameChange) => request(AccountReply, API_V1.meUsername, { method: 'PUT', body }),
      displayName: (body: DisplayNameChange) => request(AccountReply, API_V1.meDisplayName, { method: 'PUT', body }),
      settings: () => request(PlayerSettingsReply, API_V1.meSettings),
      saveSettings: (body: PlayerSettings) => request(PlayerSettingsReply, API_V1.meSettings, { method: 'PUT', body }),
    },
    // the game loop: the server decides prices, wages, needs and time; each action carries its own key
    play: {
      state: () => request(PlayReply, API_V1.mePlay),
      travel: (body: { placeId: string; mode: TravelMode }, idempotencyKey: string) =>
        request(PlayReply, API_V1.mePlayTravel, { method: 'POST', body, idempotencyKey }),
      act: (body: { activityId: string; choice?: string }, idempotencyKey: string) =>
        request(PlayReply, API_V1.mePlayActivities, { method: 'POST', body, idempotencyKey }),
      // on the way somewhere: arrive now
      skip: () => request(PlayReply, API_V1.mePlaySkip, { method: 'POST', body: {} }),
      cancel: () => request(PlayReply, API_V1.mePlayCancel, { method: 'POST', body: {} }),
      // book a flight between the game's airports, and the latest one (settled once landed)
      fly: (body: FlyRequest, idempotencyKey: string) =>
        request(FlyReply, API_V1.mePlayFly, { method: 'POST', body, idempotencyKey }),
      flight: () => request(FlightReply, API_V1.meFlight),
      // a train from King's Cross (the latest trip is `flight` too)
      train: (body: TrainRequest, idempotencyKey: string) =>
        request(FlyReply, API_V1.mePlayTrain, { method: 'POST', body, idempotencyKey }),
      // "Bring someone along": offer seats on my flight; the seats saved for me, taken or turned down
      offerSeats: (body: OfferSeatsRequest, idempotencyKey: string) =>
        request(FlyReply, API_V1.meFlightOffer, { method: 'POST', body, idempotencyKey }),
      seatOffers: () => request(SeatOffersReply, API_V1.meFlightOffers),
      takeSeat: (offerId: string, idempotencyKey: string) =>
        request(FlyReply, API_V1.meFlightOfferAccept(offerId), { method: 'POST', body: {}, idempotencyKey }),
      declineSeat: (offerId: string) =>
        request(SeatOffersReply, API_V1.meFlightOfferDecline(offerId), { method: 'POST', body: {} }),
      // my own jet: the one on the private apron, buying one, selling it back
      jet: () => request(MyJetReply, API_V1.meJet),
      buyJet: (body: BuyJetRequest, idempotencyKey: string) =>
        request(MyJetReply, API_V1.meJetBuy, { method: 'POST', body, idempotencyKey }),
      sellJet: () => request(MyJetReply, API_V1.meJetSell, { method: 'POST', body: {} }),
    },
    companyDetails: () => request(CompanyDetailsReply, API_V1.companyDetails),
    worldFacts: () => request(WorldFactsReply, API_V1.worldFacts),
    // the Mayor, the policy in force and the announcement on everyone's map
    govBanner: () => request(GovBannerReply, API_V1.govBanner),
    consentSettings: () => request(ConsentSettingsReply, API_V1.consentSettings),
    // the page says hello once a minute (counts online and visits; nothing is stored on the device)
    presence: (body: Presence) => request(z.object({ ok: z.boolean() }), API_V1.presence, { method: 'POST', body }),
    siteStats: () => request(PublicStatsReply, API_V1.siteStats),
    // delete the signed-in player's account for good (typed confirmation)
    deleteAccount: (body: AccountDelete) => request(empty, API_V1.me, { method: 'DELETE', body }),
    admin: {
      me: () => request(AdminSessionReply, API_V1.adminMe),
      companyDetails: () => request(CompanyDetailsReply, API_V1.adminCompanyDetails),
      saveCompanyDetails: (body: CompanyDetails) =>
        request(CompanyDetailsReply, API_V1.adminCompanyDetails, { method: 'PUT', body }),
      worldFacts: () => request(WorldFactsReply, API_V1.adminWorldFacts),
      consentSettings: () => request(ConsentSettingsReply, API_V1.adminConsentSettings),
      siteStats: () => request(AdminStatsReply, API_V1.adminSiteStats),
      chatReports: (all = false) => request(ChatReportsReply, `${API_V1.adminChatReports}${all ? '?all=1' : ''}`),
      gistReports: (all = false) => request(GistReportsReply, `${API_V1.adminGistReports}${all ? '?all=1' : ''}`),
      decideGistReport: (id: string, body: GistDecision) =>
        request(GistReportsReply, API_V1.adminGistReport(id), { method: 'POST', body }),
      gistPhotos: (all = false) => request(GistPhotosReply, `${API_V1.adminGistPhotos}${all ? '?all=1' : ''}`),
      decideGistPhoto: (id: string, body: GistPhotoDecision) =>
        request(GistPhotosReply, API_V1.adminGistPhoto(id), { method: 'POST', body }),
      gistNews: () => request(GistNewsReply, API_V1.adminGistNews),
      gov: () => request(GovTeamReply, API_V1.adminGov),
      govAction: (body: GovTeamAction) => request(GovTeamReply, API_V1.adminGov, { method: 'POST', body }),
      postGistNews: (text: string, idempotencyKey: string) =>
        request(GistNewsReply, API_V1.adminGistNews, { method: 'POST', body: { text }, idempotencyKey }),
      hustleReports: (all = false) => request(HustleReportsReply, `${API_V1.adminHustleReports}${all ? '?all=1' : ''}`),
      decideHustleReport: (jobId: string, body: HustleDecision) =>
        request(HustleReportsReply, API_V1.adminHustleReport(jobId), { method: 'POST', body }),
      decideChatReport: (reportId: string, body: ChatReportDecision) =>
        request(ChatReportsReply, API_V1.adminChatReport(reportId), { method: 'POST', body }),
      // the game's content: the published version, the draft and the history
      content: () => request(AdminContentReply, API_V1.adminContent),
      saveContentDraft: (body: SaveDraftBody) =>
        request(AdminContentReply, API_V1.adminContentDraft, { method: 'PUT', body }),
      discardContentDraft: () => request(AdminContentReply, API_V1.adminContentDraft, { method: 'DELETE' }),
      publishContent: (note?: string) =>
        request(AdminContentReply, API_V1.adminContentPublish, { method: 'POST', body: note ? { note } : {} }),
      restoreContent: (versionId: string) =>
        request(AdminContentReply, API_V1.adminContentRestore(versionId), { method: 'POST', body: {} }),
      teamPosts: () => request(TeamAdminReply, API_V1.adminTeamPosts),
      postToTeam: (body: TeamPostCreate) => request(TeamAdminReply, API_V1.adminTeamPosts, { method: 'POST', body }),
      takeDownTeamPost: (postId: string) => request(TeamAdminReply, API_V1.adminTeamPost(postId), { method: 'DELETE' }),
      players: (query: { q?: string; status?: string; page?: number }) =>
        request(PlayerListReply, `${API_V1.adminPlayers}${searchOf({ ...query, page: query.page || undefined })}`),
      player: (userId: string) => request(PlayerDetailReply, API_V1.adminPlayer(userId)),
      actOnPlayer: (userId: string, body: PlayerAction) =>
        request(PlayerActionReply, API_V1.adminPlayerActions(userId), { method: 'POST', body }),
      saveSiteStats: (body: SiteStatsSettings) =>
        request(AdminStatsReply, API_V1.adminSiteStats, { method: 'PUT', body }),
      saveConsentSettings: (body: ConsentSettingsUpdate) =>
        request(ConsentSettingsReply, API_V1.adminConsentSettings, { method: 'PUT', body }),
      saveWorldFacts: (body: WorldFacts) => request(WorldFactsReply, API_V1.adminWorldFacts, { method: 'PUT', body }),
      adverts: (show: 'waiting' | 'live' | 'all') =>
        request(AdminAdvertsReply, API_V1.adminAdverts, { query: { show } }),
      decideAdvert: (advertId: string, body: AdvertDecision) =>
        request(AdminAdvertsReply, API_V1.adminAdvert(advertId), { method: 'POST', body }),
      advertSettings: () => request(AdvertSettingsReply, API_V1.adminAdvertSettings),
      saveAdvertSettings: (body: AdvertSettings) =>
        request(AdvertSettingsReply, API_V1.adminAdvertSettings, { method: 'PUT', body }),
      advertPicture: picture,
      sessionSettings: () => request(AdminSessionSettingsReply, API_V1.adminSessionSettings),
      saveSessionSettings: (body: AdminSessionSettings) =>
        request(AdminSessionSettingsReply, API_V1.adminSessionSettings, { method: 'PUT', body }),
      twoStep: {
        setup: () => request(TwoStepSetupReply, API_V1.adminTwoStepSetup, { method: 'POST' }),
        confirm: (body: TwoStepCode) =>
          request(RecoveryCodesReply, API_V1.adminTwoStepConfirm, { method: 'POST', body }),
        verify: (body: TwoStepCode) =>
          request(AdminSessionReply.shape.twoStep, API_V1.adminTwoStepVerify, { method: 'POST', body }),
        newRecoveryCodes: () => request(RecoveryCodesReply, API_V1.adminTwoStepRecoveryCodes, { method: 'POST' }),
        resetFor: (adminId: string) => request(empty, API_V1.adminAdminTwoStep(adminId), { method: 'DELETE' }),
      },
      listAdmins: () => request(AdminListReply, API_V1.adminAdmins),
      createAdmin: (body: AdminCreate) => request(AdminMeReply, API_V1.adminAdmins, { method: 'POST', body }),
      setAdminRoles: (id: string, body: AdminRolesUpdate) =>
        request(AdminMeReply, API_V1.adminAdminRoles(id), { method: 'PUT', body }),
      disableAdmin: (id: string) => request(empty, API_V1.adminAdmin(id), { method: 'DELETE' }),
      listRoles: () => request(RoleListReply, API_V1.adminRoles),
      createRole: (body: RoleCreate) => request(RoleListReply, API_V1.adminRoles, { method: 'POST', body }),
      updateRole: (id: string, body: RoleUpdate) =>
        request(RoleListReply, API_V1.adminRole(id), { method: 'PATCH', body }),
      audit: (query: { limit?: number; before?: string; action?: string } = {}) =>
        request(AuditListReply, API_V1.adminAudit, { query }),
      economy: {
        summary: () => request(EconomySummaryReply, API_V1.adminEconomy),
        settings: () => request(EconomySettingsReply, API_V1.adminEconomySettings),
        saveSettings: (body: EconomySettings) =>
          request(EconomySettingsReply, API_V1.adminEconomySettings, { method: 'PUT', body }),
        findPlayer: (q: string) => request(PlayerMoneyReply, API_V1.adminLedgerPlayers, { query: { q } }),
        entries: (accountId: string, before?: number) =>
          request(LedgerEntriesReply, API_V1.adminLedgerAccountEntries(accountId), { query: { before } }),
        transaction: (id: string) => request(LedgerTransactionReply, API_V1.adminLedgerTransaction(id)),
        freeze: (accountId: string, reason: string) =>
          request(accountReply, API_V1.adminLedgerAccountFreeze(accountId), { method: 'POST', body: { reason } }),
        unfreeze: (accountId: string, reason: string) =>
          request(accountReply, API_V1.adminLedgerAccountFreeze(accountId), { method: 'DELETE', body: { reason } }),
        adjust: (body: AdjustmentRequest, idempotencyKey: string) =>
          request(MoneyActionReply, API_V1.adminLedgerAdjustments, { method: 'POST', body, idempotencyKey }),
        reverse: (transactionId: string, reason: string, idempotencyKey: string) =>
          request(MoneyActionReply, API_V1.adminLedgerReversal(transactionId), {
            method: 'POST',
            body: { reason },
            idempotencyKey,
          }),
        approvals: () => request(LedgerApprovalsReply, API_V1.adminLedgerApprovals),
        decide: (id: string, body: ApprovalDecision) =>
          request(approvalDecided, API_V1.adminLedgerApprovalDecision(id), { method: 'POST', body }),
        // large transfers between players waiting for a check
        transfers: () => request(AdminTransfersReply, API_V1.adminTransfers),
        decideTransfer: (id: string, body: TransferDecision) =>
          request(z.object({ transfer: AdminTransferView }), API_V1.adminTransferDecision(id), {
            method: 'POST',
            body,
          }),
      },
    },
  };
}
export type ApiClient = ReturnType<typeof createApiClient>;
const accountReply = z.object({ account: LedgerAccountView });
const approvalDecided = z.object({ status: z.string(), resultTransactionId: z.string().nullable() });
